import React from 'react';
import { Clock, MinusCircle, AlertTriangle, Layers, Hammer, HelpCircle } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type { HonestStateStr } from '@/lib/promoTypes';
import { GlossaryTip } from '@/components/layout/GlossaryTip';
import { useGlossary, lookupTerm } from '@/hooks/useGlossary';

/* SPEC-434 — the single honesty primitive.
 *
 * An awaiting / insufficient / not-applicable STATE renders as an explicit LABELLED
 * pill — never a 0, a bare em-dash, or a fabricated number. The label is always text
 * (colour is never the only signal — a11y `color-not-only`), and every colour class is
 * a token pair already cleared by the CI contrast guard (matches StatCard/Badge).
 *
 * `state: 'VALID'` means a real number exists — callers render THAT, not this pill; a
 * VALID passed here is shown as a quiet "Live" tick only as a last resort. */

type Tone = 'neutral' | 'info' | 'warning' | 'critical' | 'good';

const TONE_CLASS: Record<Tone, string> = {
  neutral: 'text-[var(--text-secondary)] bg-[var(--bg-overlay)]',
  info: 'text-blue-400 bg-blue-500/10',
  warning: 'text-amber-400 bg-amber-500/10',
  critical: 'text-red-400 bg-red-500/10',
  good: 'text-green-400 bg-green-500/10',
};

interface Meta {
  label: string;
  tone: Tone;
  icon: LucideIcon;
}

const STATE_META: Record<string, Meta> = {
  VALID: { label: 'Live', tone: 'good', icon: MinusCircle },
  INSUFFICIENT_DATA: { label: 'Insufficient data', tone: 'neutral', icon: MinusCircle },
  NOT_APPLICABLE: { label: 'Not applicable', tone: 'neutral', icon: MinusCircle },
  BASELINE_UNAVAILABLE: { label: 'No baseline yet', tone: 'neutral', icon: MinusCircle },
  BASE_ONLY: { label: 'Base only', tone: 'neutral', icon: MinusCircle },
  AWAITING_ACTUALS: { label: 'Awaiting actuals', tone: 'neutral', icon: Clock },
  AWAITING_SOURCE: { label: 'Awaiting source', tone: 'neutral', icon: Clock },
  AWAITING_FEED: { label: 'Waiting on the feed', tone: 'neutral', icon: Clock },
  AWAITING_PHASE_4: { label: 'Available in Phase 4', tone: 'info', icon: Hammer },
  STALE: { label: 'Stale', tone: 'warning', icon: Clock },
  MIXED: { label: 'Mixed sources', tone: 'warning', icon: Layers },
  // SPEC-442: the book carries this product and no promotion runs on it. Distinct from
  // "insufficient data", which would read as "the engine failed here".
  NO_PROMOTION: { label: 'No promotion running', tone: 'neutral', icon: MinusCircle },
  // SPEC-442: the figure is stated per promotion line, not once for the selection.
  PER_LINE: { label: 'Stated per line', tone: 'neutral', icon: Layers },
  // SPEC-440/442: the engine has not worked this selection out — distinct from "it worked it
  // out and found nothing", which is what NO_PROMOTION means. The token is SPEC-440's, shared
  // with the full-book workbench: one word, one meaning.
  AWAITING_ENGINE_RUN: { label: 'Awaiting an engine run', tone: 'neutral', icon: Clock },
  // SPEC-442: the serving run predates per-line detail, so the panel is absent because the RUN
  // is old — NEVER because the evidence was thin.
  PANELS_NOT_RECORDED: { label: 'Not recorded by this run', tone: 'neutral', icon: Clock },
  FAILED: { label: 'Failed', tone: 'critical', icon: AlertTriangle },
  INVALID: { label: 'Invalid', tone: 'critical', icon: AlertTriangle },
  /* SPEC-440 — remaining full-book Workbench states (NO_PROMOTION + AWAITING_ENGINE_RUN are
   * defined once above — merge dedupe, one token one entry). */
  NO_FORECAST_IN_WINDOW: { label: 'None in these weeks', tone: 'neutral', icon: Clock },
  NOT_IN_BOOK: { label: 'Not in the book', tone: 'neutral', icon: MinusCircle },
  // SPEC-519: the latest promotion run did not include this promotion, so it has no uplift.
  NOT_IN_RUN: { label: 'Not in the latest run', tone: 'neutral', icon: MinusCircle },
  // The book was never consulted — a different answer from "not in the book".
  BOOK_NOT_CHECKED: { label: 'Book not checked', tone: 'neutral', icon: HelpCircle },
  // A measure that exists only per LINE (the §39 sub-components, the naive basis). On a WEEK
  // row it is neither present nor missing — it is a different grain, and the line view holds it.
  LINE_LEVEL_ONLY: { label: 'See line view', tone: 'neutral', icon: Layers },
  NO_PUBLICATION: { label: 'No published book', tone: 'warning', icon: AlertTriangle },
  NOT_INITIALISED: { label: 'Not set up yet', tone: 'warning', icon: AlertTriangle },
  // SPEC-474: the source record leaves this field blank — not zero, not a failure, and not
  // "too little history" (INSUFFICIENT_DATA). The legacy pages' bare dashes become this.
  NOT_RECORDED: { label: 'Not recorded', tone: 'neutral', icon: MinusCircle },
  // SPEC-580: plain words for two SPEC-578 / Codex r5 states the page now names.
  LAUNCHED_ON_PROMOTION: { label: 'Launched on a promotion', tone: 'neutral', icon: MinusCircle },
  NOT_YET_TAGGED: { label: 'Not yet tagged', tone: 'neutral', icon: Clock },
  PRE_TAGGING: { label: 'Before order tagging', tone: 'neutral', icon: Clock },
  // SPEC-572: no feed carries this field (the promotion's account manager) — not "on its way",
  // which is what "Awaiting source" says.
  NOT_SENT_BY_PORTAL: { label: 'Not sent by the portal', tone: 'neutral', icon: MinusCircle },
  // SPEC-492: an open PO line whose served arrival date is before the portal's last update —
  // the same overdue line the overdue lists show, with the reason named.
  EXPIRED_PROMISE: { label: 'Arrival date passed', tone: 'warning', icon: Clock },
  // SPEC-495: a reorder line with no purchase cost on record — not valued, left out of the
  // total (which says how many). Never "£1" or "£0".
  NO_COST_ON_RECORD: { label: 'No cost on record', tone: 'neutral', icon: MinusCircle },
  // SPEC-499 (G71): the basis behind a demand-of-record figure once Box A is the demand of
  // record. A fallback is labelled on every reader; no signal is a held buy, never a 0.
  AGREED_PLAN: { label: 'Agreed plan', tone: 'good', icon: MinusCircle },
  ROS_FALLBACK: { label: 'Rate-of-sale fallback', tone: 'warning', icon: AlertTriangle },
  NO_DEMAND_SIGNAL: { label: 'No demand signal', tone: 'critical', icon: AlertTriangle },
  // SPEC-496: the read behind this figure failed — the figure is unknown, never 0 / empty.
  READ_FAILED: { label: 'Could not be read', tone: 'warning', icon: AlertTriangle },
  // SPEC-496: the live purchase-order feed is not in this database, so a figure measured from
  // it is not worked out (the frozen older PO record is never read in its place).
  NO_PORTAL_FEED: { label: 'No live PO feed', tone: 'neutral', icon: Clock },
  // SPEC-497: the planner's per-PO changes could not be read, so inbound is not counted.
  OVERRIDES_UNREADABLE: { label: 'Planner changes unreadable', tone: 'warning', icon: AlertTriangle },
  // SPEC-496 (G75 'legacy' position): the figure was worked out from the frozen PO table —
  // labelled on every row so the old source is never silent.
  LEGACY_FROZEN: { label: 'From the frozen PO table', tone: 'warning', icon: Clock },
  // NOTE (SPEC-453): `OVERLAPPING_WINDOW` deliberately has NO entry here. The engine records
  // it only as `evidence_excluded.reason` — never as a cell's `*_state` — so a pill for it
  // would be copy nothing can render. That refusal reaches the reader as a sentence instead,
  // through `promo_absence_reason` (routes/promo.py `_promo_absence_cause`).
};

function metaFor(state: HonestStateStr): Meta {
  return STATE_META[state] ?? { label: prettify(state), tone: 'neutral', icon: HelpCircle };
}

/** Turn an unknown UPPER_SNAKE state into a readable label rather than crashing. */
function prettify(state: string): string {
  if (!state) return 'Unknown';
  const s = state.replace(/_/g, ' ').toLowerCase();
  return s.charAt(0).toUpperCase() + s.slice(1);
}

interface HonestStateProps {
  state: HonestStateStr;
  /** Override the default label (e.g. "Awaiting actuals" for a specific metric). */
  label?: string;
  size?: 'sm' | 'md';
  className?: string;
  title?: string;
  /** SPEC-453 — THE SPECIFIC cause the run recorded for THIS row, in planner words
   *  (an excluded reason, the baseline source, "the run predates per-line detail", …).
   *  Pass ONLY a recorded value: a pill with no recorded cause must say so, never guess.
   *  Absent/blank → the ⓘ shows the general meaning plus "no further detail recorded". */
  detail?: string | null;
}

/* SPEC-453 — a state pill EXPLAINS ITSELF.
 *
 * Param, verbatim: "when I see insufficient data, I don't know what that means". So every
 * pill carries a ⓘ with two things: (a) what the state means in general, read from the ONE
 * glossary by the exact state token, and (b) the specific cause this run recorded for this
 * row, where the payload carries one. The engines already record causes — we surface them;
 * we never invent one. A state with no recorded cause says "no further detail recorded".
 *
 * The ⓘ hides itself when the glossary has no entry for the token (GlossaryTip's rule), so
 * an unknown state still renders a correct pill and simply offers no explanation. */
const NO_DETAIL_NOTE = 'No further detail recorded for this row.';

export function HonestState({
  state, label, size = 'md', className = '', title, detail,
}: HonestStateProps) {
  const meta = metaFor(state);
  const pad = size === 'sm' ? 'px-1.5 py-0.5 text-[11px] gap-1' : 'px-2 py-0.5 text-xs gap-1.5';
  const iconSize = size === 'sm' ? 'w-3 h-3' : 'w-3.5 h-3.5';
  const Icon = meta.icon;
  const trimmed = detail && detail.trim() ? detail.trim() : null;
  // SPEC-580 addendum (2): with an ⓘ explainer on the pill, the browser's own tooltip is left
  // off — two tooltips stacked over each other. The explainer carries the same words.
  const hasTip = Boolean(lookupTerm(useGlossary(), state));
  return (
    <span
      className={`inline-flex items-center rounded-full font-medium ${pad} ${TONE_CLASS[meta.tone]} ${className}`}
      // A native tooltip as well as the ⓘ: it reaches a reader who hovers the pill itself
      // rather than the icon, and it still never shows a cause the payload did not carry.
      // Review: only the generated title (or one that repeats the ⓘ's detail) gives way to the ⓘ;
      // a caller's own different words are kept.
      title={hasTip && (title === undefined || title === trimmed) ? undefined
        : (title ?? (trimmed ? `${meta.label} — ${trimmed}` : meta.label))}
    >
      <Icon className={`${iconSize} flex-shrink-0`} />
      {label ?? meta.label}
      <GlossaryTip
        term={state}
        detail={trimmed}
        noDetailNote={NO_DETAIL_NOTE}
        align="center"
        label={`What does “${label ?? meta.label}” mean here?`}
        className="ml-0.5"
      />
    </span>
  );
}

/** True when a wire value should render its number (VALID with a present figure) rather
 *  than an honest-state pill. Keeps the "NULL ≠ 0" rule in one place. */
export function hasNumber(cases: string | null, state?: HonestStateStr): boolean {
  if (cases === null || cases === undefined) return false;
  if (state && state !== 'VALID') return false;
  return true;
}
